import {
  ErrorSchemaType,
  OrderListResponseType,
  OrderV2Dto,
} from "@sellar-io/api-v2/types";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { HttpError } from "~/utils/httpClient";
import { useFirebaseUser } from "../../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../../apiV2Request";
import { ServerStateKeys } from "../../ServerStateKeys";

export const useOrderUpdate = (id: number) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const queryClient = useQueryClient();

  return useMutation<
    OrderV2Dto.OrderUpdateResponseType,
    HttpError<{ message: string; details: ErrorSchemaType }>,
    OrderV2Dto.OrderUpdateRequestType
  >({
    mutationFn: (data: OrderV2Dto.OrderUpdateRequestType) =>
      apiV2Request({
        user,
        business,
        method: "put",
        path: `orders/v2/${id}`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse(response)
            .data as OrderV2Dto.OrderUpdateResponseType,
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.Orders, id],
      });
      queryClient.invalidateQueries({
        queryKey: [business?.id, ServerStateKeys.OrderComments],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.OrderEvents, id],
      });
      // TODO: this needs to reconsidered, what if the order integration takes > 1 second to get updated with an integration tracking number
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [business?.id, ServerStateKeys.OrderIntegrationValue, id],
        });
      }, 1000);

      queryClient.setQueriesData<InfiniteData<OrderListResponseType>>(
        {
          predicate: ({ queryKey }) => {
            return (
              queryKey[0] === user?.uid &&
              queryKey[1] === business?.id &&
              queryKey[2] === ServerStateKeys.Orders &&
              typeof queryKey[3] === "object"
            );
          },
        },
        (data) => {
          if (!data?.pages) return data;
          return {
            ...data,
            pages: data.pages.map((page) =>
              page.map((order) =>
                order.id === response.id ? response : order,
              ),
            ),
          };
        },
      );
    },
  });
};
